import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcRegistrationV1AccountQueryServiceAccountList } from '@interxfi/api-client/generated/hooks';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { StatusBar } from '@interxfi/design-system/components/status-bar/status-bar';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { IxcRegistrationV1AccountQueryServiceAccountListFilterableFields, IxcRegistrationV1AccountStatus } from '@interxfi/types';
import { buildAccountListQuery } from '../../../../../components/data-combobox-input/entity-config/registration-account';
import { PageTabRail } from '../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../components/router/router-tabbed-navigation';
import { RegistrationAccountsTable, type RegistrationAccountTableHeaderProps } from '../../../../../features/registration/account/accounts-table';
import { CreateAccountDrawer } from '../../../../../features/registration/account/create-account-drawer';
import { REGISTRATION_ACCOUNT_BASE_PATH } from '../../../../../features/registration/account/registration-account-routing';
import { getPageHead } from '../../../../../util/routing/meta';
import { useRollupViewContext } from '../../../../../util/view-context';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Registration,
      label: 'Accounts',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Registration>,
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'account-list',
        label: 'Registration',
        to: REGISTRATION_ACCOUNT_BASE_PATH,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Accounts'),
  component: AccountList,
});

function TableHeader({ filter, filterProps, search }: RegistrationAccountTableHeaderProps) {
  const { data } = useIxcRegistrationV1AccountQueryServiceAccountList(buildAccountListQuery({ status: IxcRegistrationV1AccountStatus.Pending }));
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const hasPendingAccounts = Boolean(data?.pages?.find((page) => (page?.account?.length || 0) > 0));
  const statusFilter = filterProps?.values?.[IxcRegistrationV1AccountQueryServiceAccountListFilterableFields.Status];
  const hasOnlyPendingValueInStatusFilterActive = statusFilter
    ? 'exact' in statusFilter
      ? statusFilter.exact === IxcRegistrationV1AccountStatus.Pending
      : 'multiple' in statusFilter && statusFilter.multiple.length === 1 && statusFilter.multiple.includes(IxcRegistrationV1AccountStatus.Pending)
    : false;
  const filterValues = (filterProps?.values ?? {}) as Exclude<RegistrationAccountTableHeaderProps['filterProps'], undefined>['values'];

  return (
    <VerticalStack gap={7}>
      <PageTabRail
        actions={
          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            <RequiresAccess write>
              <CreateAccountDrawer trigger={<Button affixIcon={<PlusIcon aria-hidden />} aria-label="New Account" size="small" />} />
            </RequiresAccess>
            {filter}
          </HorizontalStack>
        }
        group={TabbedNavigationGroup.Registration}
      />

      {hasPendingAccounts && activeTenantIsClearing && !hasOnlyPendingValueInStatusFilterActive ? (
        <StatusBar
          actions={
            activeTenantIsClearing
              ? [
                  {
                    id: 'showPending',
                    triggerStyle: 'button',
                    ctaText: 'Show All',
                    type: 'button',
                    button: {
                      onClick: () => {
                        filterProps?.onFilterChange?.({
                          ...filterValues!,
                          [IxcRegistrationV1AccountQueryServiceAccountListFilterableFields.Status]: {
                            multiple: [IxcRegistrationV1AccountStatus.Pending],
                          },
                        });
                      },
                    },
                  },
                ]
              : undefined
          }
          title="Accounts are pending approval"
          variant="text"
        />
      ) : null}

      {search}
    </VerticalStack>
  );
}

function AccountList() {
  const viewContext = useRollupViewContext();

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <RegistrationAccountsTable HeaderActions={TableHeader} view={viewContext} />
      </PageLayout.Section>
    </PageLayout>
  );
}
